import React from 'react';
import { Alert, Box, Grid, Typography } from 'housecall-ui';
import { formatPhoneNumber } from 'housecall-ui/dist/utils/TextFormat';

import { TimeSlot, TimeSlotDict } from '../../api/api';
import { useOrgData } from '../../hooks/useOrgData';
import { actions } from '../../state/context';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';

import { TimeSlotElement } from './TimeSlotElement';
import { TimeSlotsSkeleton } from './TimeSlotsSkeleton';

type Props = {
  /**
   * YYYY-MM-DD
   */
  date?: string;
  dateFrom: Date;
  dateTo: Date;
  isExactTime: boolean;
  timeSlots: TimeSlotDict;
  isLoading: boolean;
  isError: boolean;
};

const isTheSameTimeslot = (firstTimeSlot: TimeSlot, nextTimeSlot?: TimeSlot) => {
  return firstTimeSlot.from === nextTimeSlot?.from && firstTimeSlot.to === nextTimeSlot?.to;
};

export const TimeSlots = ({ date, isExactTime, timeSlots, isLoading, isError }: Props) => {
  const { state, dispatch } = useOnlineBookingReducer();
  const { data: orgData } = useOrgData(state);
  const {
    arrivalWindow: { time: selectedTimeSlot },
    unavailableDateError,
  } = state;

  if (isLoading) {
    return <TimeSlotsSkeleton />;
  }

  const timeSlotsToRender = timeSlots && date ? timeSlots[date] : undefined;
  const handleTimeSlotClick = (timeSlotClicked: TimeSlot) => {
    dispatch(actions.selectArrivalTime(timeSlotClicked));
  };

  const timeSlotsUnavailable = isError || (!isLoading && unavailableDateError && !timeSlots);

  return (
    <Box mb={1}>
      <Typography type="body1" gutterBottom>
        {isExactTime ? 'We’ll arrive at:' : 'We’ll arrive within the selected timeframe:'}
      </Typography>
      <Grid container spacing={2}>
        {timeSlotsUnavailable ? (
          <Grid item xs={12}>
            <Alert severity="error" data-testid="time-slot-alert">
              We currently don&apos;t have available time slots for the selected services. Please{' '}
              {orgData?.phoneNumber ? `call ${formatPhoneNumber(orgData.phoneNumber)}` : `e-mail ${orgData?.email}`} to
              schedule an appointment, or consider booking fewer services to find an available time.
            </Alert>
          </Grid>
        ) : (
          timeSlotsToRender?.map((timeslot) => (
            <Grid key={timeslot.from} item md={4} xs={6}>
              <TimeSlotElement
                timeslot={isExactTime ? timeslot.from : `${timeslot.from.slice(0, -2)} - ${timeslot.to}`}
                selected={isTheSameTimeslot(timeslot, selectedTimeSlot)}
                onClick={() => {
                  handleTimeSlotClick(timeslot);
                }}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};
